/**
 * «Финансы → Контрагенты»: с кем и сколько — приход, расход, первая и
 * последняя операция, город. Клик — все операции с контрагентом за тот же
 * период. Таблица — `CounterpartyTable`, общая с «Аналитикой» операций.
 * Контрагент — ИНН; без ИНН (переводы физлицам) — имя.
 *
 * Фильтры живут в адресе: ссылкой можно поделиться.
 *
 * Спека: `docs/specs/2026-09-30-business-expenses-counterparties-design.md`.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { CounterpartyTable } from "@/components/CounterpartyTable";
import { DateRangePicker, withPeriod } from "@/components/DateRangePicker";
import { EntrepreneurPicker, entrepreneurIds, withEntrepreneurs } from "@/components/EntrepreneurPicker";
import { NoAccess } from "@/components/NoAccess";
import { PNL, useCan } from "@/lib/access";
import {
  type Article,
  type BankChannel,
  CHANNEL_LABEL,
  type Counterparty,
  counterpartyOperationsUrl,
  loadArticles,
  loadCounterparties,
} from "@/lib/pnl";
import { type DirectoryRow, ENTREPRENEURS, loadDirectory } from "@/lib/statements";

const INPUT =
  "h-8 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-[12.5px] outline-none focus:border-border-strong";
export function CounterpartiesPage() {
  return useCan(PNL) ? <CounterpartiesScreen /> : <NoAccess what="контрагентам" />;
}

function CounterpartiesScreen() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [entrepreneurs, setEntrepreneurs] = useState<DirectoryRow[]>([]);
  const [articles, setArticles] = useState<Article[]>([]);
  const [result, setResult] = useState<{ truncated: boolean; rows: Counterparty[] } | null>(null);
  const [failure, setFailure] = useState("");
  const [searchDraft, setSearchDraft] = useState(params.get("search") ?? "");

  const filter = useMemo(
    () => ({
      search: params.get("search") ?? undefined,
      entrepreneur_id: entrepreneurIds(params),
      // Из P&L без безнала ИП — только личные счета.
      business: params.get("business") !== "false",
      personal: params.get("personal") !== "false",
      // Из P&L: «Не разнесено» по контрагентам — статья и направление.
      article_id: params.get("article_id") ? Number(params.get("article_id")) : undefined,
      direction: (params.get("direction") as "in" | "out" | null) ?? undefined,
      // Из строки P&L «Не разнесено: расходы» — как ушли деньги.
      channel: (params.get("channel") as BankChannel | null) ?? undefined,
      date_from: params.get("date_from") ?? undefined,
      date_to: params.get("date_to") ?? undefined,
    }),
    [params],
  );

  const setFilter = (key: string, value: string | undefined) => {
    const next = new URLSearchParams(params);
    if (value === undefined || value === "") next.delete(key);
    else next.set(key, value);
    setParams(next);
  };

  const setPeriod = (from: string, to: string) => setParams(withPeriod(params, from, to));

  useEffect(() => {
    loadDirectory(ENTREPRENEURS, true)
      .then(setEntrepreneurs)
      .catch((error: Error) => setFailure(error.message));
    loadArticles()
      .then(setArticles)
      .catch((error: Error) => setFailure(error.message));
  }, []);

  const articleName = articles.find((a) => a.id === filter.article_id)?.name;

  useEffect(() => {
    setResult(null);
    setFailure("");
    loadCounterparties(filter)
      .then(setResult)
      .catch((error: Error) => setFailure(error.message));
  }, [filter]);

  const open = (cp: Counterparty) => navigate(counterpartyOperationsUrl(cp, filter));

  return (
    <main className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4 [&>*]:flex-none">
      <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
        <form
          className="flex items-center gap-1"
          onSubmit={(e) => {
            e.preventDefault();
            setFilter("search", searchDraft.trim());
          }}
        >
          <input
            className={`${INPUT} w-[260px]`}
            placeholder="название, ИНН, назначение"
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
          />
        </form>
        <EntrepreneurPicker
          entrepreneurs={entrepreneurs}
          value={filter.entrepreneur_id}
          onChange={(ids) => setParams(withEntrepreneurs(params, ids))}
        />
        <label className="flex items-center gap-1.5 text-fg-muted">
          <input
            type="checkbox"
            checked={filter.personal}
            onChange={(e) => setFilter("personal", e.target.checked ? undefined : "false")}
          />
          с личными счетами
        </label>
        <span className="ml-2 text-fg-faint">период</span>
        <DateRangePicker label="период" from={filter.date_from ?? ""} to={filter.date_to ?? ""} onChange={setPeriod} />
      </div>

      {filter.article_id || filter.direction || filter.channel ? (
        <div className="flex items-center gap-2 text-[12px] text-fg-muted">
          <span>
            {filter.article_id ? `Статья: ${articleName ?? `№${filter.article_id}`}` : ""}
            {filter.direction ? ` · только ${filter.direction === "out" ? "расходы" : "приходы"}` : ""}
            {filter.channel ? ` · ${CHANNEL_LABEL[filter.channel]}` : ""} — разнести
            пачкой: откройте контрагента и сделайте правило
          </span>
          <button
            type="button"
            className="text-accent-text hover:underline"
            onClick={() => {
              const next = new URLSearchParams(params);
              next.delete("article_id");
              next.delete("direction");
              next.delete("channel");
              setParams(next);
            }}
          >
            снять
          </button>
        </div>
      ) : null}

      {failure ? (
        <div role="alert" className="rounded-[var(--radius-ui)] border border-err/30 bg-err-bg px-3 py-2 text-[12.5px] text-err">
          {failure}
        </div>
      ) : null}

      {result === null ? (
        <div className="text-[12.5px] text-fg-muted">…</div>
      ) : result.rows.length === 0 ? (
        <div className="text-[12.5px] text-fg-muted">Никого не нашлось.</div>
      ) : (
        <CounterpartyTable result={result} onResult={setResult} onOpen={open} onFailure={setFailure} />
      )}
    </main>
  );
}
